// Centralized API Service for all API calls

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    // Check if response body has already been read
    if (response.bodyUsed) {
      console.warn('Response body already used, cannot read again');
      return {
        success: false,
        error: 'Response body already consumed'
      };
    }
    
    const data = await response.json();
    
    if (data.isSuccess) {
      return {
        success: true,
        data: data.data
      };
    } else {
      return {
        success: false,
        error: 'Failed to fetch geolocation'
      };
    }
  } catch (error) {
    console.error('Geolocation API error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Submit lead to CRM
 * @param {Object} leadData - Lead information
 * @returns {Object} Response with success status and data
 */
export const submitLead = async (leadData) => {
  try {
    // Ensure phone number is numeric only
    const phoneNumber = leadData.phoneNumber.replace(/\D/g, '');
    
    // Prepare payload
    const payload = {
      name: leadData.name,
      email: leadData.email,
      countryDialCode: parseInt(leadData.countryDialCode),
      country: leadData.country,
      phoneNumber: phoneNumber,
      ipAddress: leadData.ipAddress || '',
      description: leadData.description || '',
      pageUrl: window.location.href,
      city: leadData.city || '',
      state: leadData.state || '',
      geoLocation: leadData.geoLocation ? JSON.stringify(leadData.geoLocation) : '',
      packageTitle: leadData.packageTitle || '',
      packageAmount: leadData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    console.log('Response status:', response.status);
    console.log('Response bodyUsed:', response.bodyUsed);

    // Check if response body has already been read
    if (response.bodyUsed) {
      console.error('Response body already used, cannot read again');
      return {
        success: false,
        error: 'Response body already consumed. Please try again.'
      };
    }

    // Clone response before reading to prevent body used error
    const responseClone = response.clone();
    let data;
    
    try {
      data = await responseClone.json();
    } catch (parseError) {
      console.error('Error parsing response:', parseError);
      return {
        success: false,
        error: 'Failed to parse server response'
      };
    }

    console.log('Response data:', data);

    // Handle different status codes
    if (response.status === 200 && data.isSuccess) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message
      };
    } else if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    } else if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data',
        statusCode: 400
      };
    } else {
      return {
        success: false,
        error: 'An unexpected error occurred',
        statusCode: response.status
      };
    }
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      error: error.message || 'Network error occurred'
    };
  }
};

/**
 * Get country dial code from country code
 * @param {string} countryCode - Two letter country code (e.g., 'US', 'PK')
 * @returns {number} Dial code
 */
export const getDialCodeFromCountry = (countryCode) => {
  const dialCodes = {
    'US': 1,
    'CA': 1,
    'GB': 44,
    'PK': 92,
    'IN': 91,
    'NL': 31,
    'DE': 49,
    'FR': 33,
    'AU': 61,
    'CN': 86,
    'JP': 81,
    // Add more as needed
  };
  
  return dialCodes[countryCode] || 1; // Default to US
};
